import {
  clearEmbeddedCredential,
  EmbeddedCredentialMode,
  getEmbeddedCredential,
  setEmbeddedCredential,
} from '@web-apps/utils-shared'

export const RN_WEBVIEW_URL_PARAM = 'rn_webview'
export const RN_WEBVIEW_SESSION_STORAGE_KEY = 'rn_webview'

export const RN_AUTH_READY_EVENT = 'rn-auth-ready'
export const RN_CREDENTIAL_CHANGED_EVENT = 'rn-credential-changed'

export const isRNWebView = (): boolean =>
  typeof window !== 'undefined' &&
  sessionStorage.getItem(RN_WEBVIEW_SESSION_STORAGE_KEY) === 'true'

export const getRNWebViewCredentialMode = (): EmbeddedCredentialMode | null =>
  getEmbeddedCredential()?.mode ?? null

export const hasRNWebViewCredential = (): boolean =>
  Boolean(getRNWebViewCredentialMode())

type RNAuthMessage = {
  type: 'access_code_creator_app'
  mode?: EmbeddedCredentialMode
  value?: string
  token?: string
  cookie?: string
}

const isRNAuthMessage = (data: unknown): data is RNAuthMessage =>
  data !== null &&
  typeof data === 'object' &&
  (data as Record<string, unknown>).type === 'access_code_creator_app'

const isValidCookiePair = (value: unknown): value is string =>
  typeof value === 'string' && value.indexOf('=') > 0 && !/[;\n\r]/.test(value)

const getSessionCookieDomain = (): string | null => {
  const { hostname } = window.location
  const labels = hostname.split('.')

  if (labels.length < 2) return null
  // an IP address cannot take a Domain attribute
  if (/^[\d.]+$/.test(hostname)) return null

  return `.${labels.slice(-2).join('.')}`
}

const clearStaleSessionCookies = (keptName?: string): void => {
  const domain = getSessionCookieDomain()

  document.cookie
    .split(';')
    .map((pair) => pair.trim().split('=')[0])
    .filter((name) => name.startsWith('ory_session_') && name !== keptName)
    .forEach((name) => {
      const expired = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT`

      document.cookie = expired
      if (domain) document.cookie = `${expired}; Domain=${domain}`
    })
}

const setRNWebViewSessionCookie = (cookiePair: string): void => {
  const domain = getSessionCookieDomain()

  clearStaleSessionCookies(cookiePair.split('=')[0])

  document.cookie = [
    cookiePair,
    'Path=/',
    domain && `Domain=${domain}`,
    window.location.protocol === 'https:' && 'Secure',
    'SameSite=Lax',
  ]
    .filter(Boolean)
    .join('; ')
}

const readCredential = (
  msg: RNAuthMessage
): { mode: EmbeddedCredentialMode; value: string } | null => {
  if (msg.mode && typeof msg.value === 'string' && msg.value)
    return { mode: msg.mode, value: msg.value }

  if (isValidCookiePair(msg.cookie))
    return { mode: 'cookie', value: msg.cookie }

  if (typeof msg.token === 'string' && msg.token)
    return { mode: 'token', value: msg.token }

  return null
}

const applyCredential = ({
  mode,
  value,
}: {
  mode: EmbeddedCredentialMode
  value: string
}): void => {
  if (mode === 'cookie') {
    setRNWebViewSessionCookie(value)
  } else {
    clearStaleSessionCookies()
  }

  setEmbeddedCredential({ mode, value })
}

export const clearRNWebViewCredential = (): void => {
  const hadCredential = Boolean(getEmbeddedCredential())

  clearEmbeddedCredential()
  clearStaleSessionCookies()

  if (hadCredential)
    window.dispatchEvent(new CustomEvent(RN_CREDENTIAL_CHANGED_EVENT))
}

let appliedCredentialSignature: string | null = null

export const initRNWebViewAuthListener = (): void => {
  const handleMessage = (event: MessageEvent) => {
    let parsed: unknown
    try {
      parsed =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data
    } catch {
      return
    }

    if (!isRNAuthMessage(parsed)) return

    const credential = readCredential(parsed)

    if (!credential) return

    const signature = `${credential.mode}:${credential.value}`

    if (signature === appliedCredentialSignature) {
      window.dispatchEvent(new CustomEvent(RN_AUTH_READY_EVENT))
      return
    }

    appliedCredentialSignature = signature
    applyCredential(credential)

    window.dispatchEvent(new CustomEvent(RN_AUTH_READY_EVENT))
    window.dispatchEvent(new CustomEvent(RN_CREDENTIAL_CHANGED_EVENT))
  }

  // react-native-webview's postMessage from native fires on `window` on iOS
  // but on `document` on Android, so we listen on both.
  window.addEventListener('message', handleMessage)
  document.addEventListener('message', handleMessage as EventListener)
}
